import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { IPaginationParameters, IPaginationResponse, ILookup, CollectionApiClient } from '../../../common';
import {
  ICreateCustomerProfileRequest,
  ICreateCustomerProfileResponse,
  ICustomerProfileResponse,
  IPublicOffStreetReservationResponse,
  IReservationListResponse,
  IReservationResponse,
  IReservationsPaginationParameters,
  ISettleOffStreetReservationRequest,
  IUpdateCustomerProfileRequest,
  ICustomerInvoicesPaginationParameters,
  ICustomerInvoiceListResponse
} from '../dtos';
import { ICustomerProfilesApiClient } from './i-customer-profiles-api.client';
import { customerProfilesData } from '../data/customer-profiles.data';
import { ICreateVehicleRequest, IVehicleResponse, IVehiclesBasePaginationParameters, vehiclesData } from '../../../vehicle-management/vehicles';
import { IOrderResponse, IOnStreetReservationResponse, IOnStreetReservationPaginationParameters, ordersData, IOrderPaymentRequestResponse, IMyOnStreetReservationDetailsResponse, IPublicOrderPaymentRequestResponse } from '../../../order-management';
import { ICustomerInvoiceResponse, IInvoiceResponse } from '../../../financial-management/invoices';
import {
  ICustomerViolationPaginationParameters,
  IPublicViolationResponse,
  IViolationListResponse
} from '../../../violation-management/violations/dtos';

@Injectable({
  providedIn: 'root'
})
export class CustomerProfilesInMemoryApiClient implements ICustomerProfilesApiClient {
  constructor(protected http: HttpClient) {}

  getCollectionApiClient(): CollectionApiClient {
    throw new Error(`Not Supported in in memory clients`);
  }

  create(reqeust: ICreateCustomerProfileRequest): Observable<ICreateCustomerProfileResponse> {
    return of(customerProfilesData.createCustomerProfileResponse);
  }

  update(id: string, request: IUpdateCustomerProfileRequest): Observable<void> {
    return of();
  }

  get(paginationParameters?: IPaginationParameters): Observable<IPaginationResponse<ICustomerProfileResponse>> {
    return of(customerProfilesData.customerProfilesResponse);
  }

  getById(id: string): Observable<ICustomerProfileResponse> {
    return of(customerProfilesData.customerProfileResponse);
  }

  getByPhoneNumber(phoneNumber: string): Observable<ICustomerProfileResponse> {
    return of(customerProfilesData.customerProfileResponse);
  }

  getEligibleAccounts(id: string): Observable<IPaginationResponse<ILookup<string>>> {
    return of(customerProfilesData.customerEligibleAccountsResponse);
  }

  getVehicles(
    id: string,
    paginationParameters?: IVehiclesBasePaginationParameters
  ): Observable<IPaginationResponse<IVehicleResponse>> {
    return of(vehiclesData.vehiclesResponse);
  }

  getMyVehicles(paginationParameters?: IVehiclesBasePaginationParameters): Observable<IPaginationResponse<IVehicleResponse>> {
    return of(vehiclesData.vehiclesResponse);
  }

  createVehicle(id: string, request: ICreateVehicleRequest): Observable<IVehicleResponse> {
    return of(vehiclesData.vehicleResponse);
  }

  createMyVehicle(request: ICreateVehicleRequest): Observable<IVehicleResponse> {
    return of(vehiclesData.vehicleResponse);
  }

  getMyOrders(paginationParameters?: IPaginationParameters): Observable<IPaginationResponse<IOrderResponse>> {
    return of(ordersData.ordersResponse);
  }

  getMyOnStreetReservations(paginationParameters?: IOnStreetReservationPaginationParameters): Observable<IPaginationResponse<IOnStreetReservationResponse>> {
    return of(customerProfilesData.myOnStreetReservationsResponse);
  }

  getMyOnStreetReservationById(id: string): Observable<IMyOnStreetReservationDetailsResponse> {
    return of(customerProfilesData.myOnStreetReservationDetailsResponse);
  }

  getMyViolations(paginationParameters?: ICustomerViolationPaginationParameters): Observable<IPaginationResponse<IViolationListResponse>> {
    const page = paginationParameters?.page || 1;
    const pageSize = paginationParameters?.pageSize || 6;
    const violations = this.getMockViolations().filter((violation) => {
      return !paginationParameters?.status || violation.status === paginationParameters.status;
    });
    const items = violations.slice((page - 1) * pageSize, page * pageSize);

    return of({
      items,
      paginationMetadata: {
        totalItemCount: violations.length,
        numberOfPages: Math.ceil(violations.length / pageSize),
        pageSize,
        order: paginationParameters?.sortOrder || 'Descending',
        page,
        hasNext: page * pageSize < violations.length,
        hasPrevious: page > 1
      }
    });
  }

  getMyViolationById(id: string): Observable<IPublicViolationResponse> {
    return of(this.getMockViolationDetails(id));
  }

  getMyInvoiceByOrderId(orderId: string): Observable<ICustomerInvoiceResponse> {
    return of(customerProfilesData.myInvoiceByOrderResponse);
  }

  getMyInvoices(paginationParameters?: ICustomerInvoicesPaginationParameters): Observable<IPaginationResponse<ICustomerInvoiceListResponse>> {
    return of(customerProfilesData.myInvoicesResponse);
  }

  getMyOffStreetReservations(paginationParameters?: IReservationsPaginationParameters): Observable<IPaginationResponse<IReservationListResponse>> {
    return of(customerProfilesData.reservationsResponse);
  }

  getMyOffStreetReservationById(id: string): Observable<IPublicOffStreetReservationResponse> {
    return of(customerProfilesData.publicOffStreetReservationResponse);
  }

  getMyOffStreetReservationByCode(code: string): Observable<IPublicOffStreetReservationResponse> {
    return of(customerProfilesData.publicOffStreetReservationResponse);
  }

  pickOffStreetReservation(code: string): Observable<void> {
    return of();
  }

  customerSettleOffStreetReservation(code: string, request: ISettleOffStreetReservationRequest): Observable<IPublicOrderPaymentRequestResponse> {
    return of(ordersData.createOrderPaymentRequestResponse);
  }

  anonymousSettleOffStreetReservation(code: string, request: ISettleOffStreetReservationRequest): Observable<IPublicOrderPaymentRequestResponse> {
    return of(ordersData.createOrderPaymentRequestResponse);
  }

  private getMockViolations(): IViolationListResponse[] {
    return [
      {
        id: 'mock-violation-1',
        createdOnUtc: '2024-08-10T12:00:00Z',
        modifiedOnUtc: '2024-08-10T12:00:00Z',
        autoIncrementalId: 10229335,
        vehicle: {
          vehicleId: 'vehicle-1',
          plateDigits: '3567',
          plateLetters: 'TGH'
        },
        parkingArea: {
          parkingAreaId: 'parking-area-1',
          englishName: '29 north Riyadh, 122',
          arabicName: '29 north Riyadh, 122'
        },
        pricing: {
          subtotal: { amount: 116.53, currency: 'SAR' },
          vat: {
            vatAmount: { amount: 17.47, currency: 'SAR' },
            vatPercentage: 15
          },
          total: { amount: 134, currency: 'SAR' }
        },
        status: 'Active',
        isSettled: false
      },
      {
        id: 'mock-violation-2',
        createdOnUtc: '2024-08-09T10:30:00Z',
        modifiedOnUtc: '2024-08-09T10:30:00Z',
        autoIncrementalId: 10229336,
        vehicle: {
          vehicleId: 'vehicle-2',
          plateDigits: '342',
          plateLetters: 'AAA'
        },
        parkingArea: {
          parkingAreaId: 'parking-area-2',
          englishName: 'King Fahd Road',
          arabicName: 'King Fahd Road'
        },
        pricing: {
          subtotal: { amount: 86.96, currency: 'SAR' },
          vat: {
            vatAmount: { amount: 13.04, currency: 'SAR' },
            vatPercentage: 15
          },
          total: { amount: 100, currency: 'SAR' }
        },
        status: 'Active',
        isSettled: false
      },
      {
        id: 'mock-violation-3',
        createdOnUtc: '2024-08-08T08:15:00Z',
        modifiedOnUtc: '2024-08-08T08:15:00Z',
        autoIncrementalId: 10229337,
        vehicle: {
          vehicleId: 'vehicle-3',
          plateDigits: '905',
          plateLetters: 'BCD'
        },
        parkingArea: {
          parkingAreaId: 'parking-area-3',
          englishName: 'Olaya Street',
          arabicName: 'Olaya Street'
        },
        pricing: {
          subtotal: { amount: 60, currency: 'SAR' },
          vat: {
            vatAmount: { amount: 9, currency: 'SAR' },
            vatPercentage: 15
          },
          total: { amount: 69, currency: 'SAR' }
        },
        status: 'Settled',
        isSettled: true
      }
    ];
  }

  private getMockViolationDetails(id: string): IPublicViolationResponse {
    return {
      id,
      code: '10229335',
      pricing: {
        subtotal: { amount: 86.96, currency: 'SAR' },
        vat: {
          vatAmount: { amount: 13.04, currency: 'SAR' },
          vatPercentage: 15
        },
        total: { amount: 100, currency: 'SAR' }
      },      locationName: { englishName: '29 north Riyadh, 122', arabicName: '29 north Riyadh, 122' },
      carDigits: '3567',
      carLetters: 'TGH',
      carPlateCountry: { englishName: 'Saudi Arabia', arabicName: 'Saudi Arabia' },
      carPlateCountryId: 'a7b8c9d0-e12f-4e3b-97a5-12d34f56789a',
      violationDate: '2024-08-10T12:00:00Z',
      carPlateType: { englishName: 'Private', arabicName: 'Private' },
      violationItems: [
        {
          price: {
            subtotal: { amount: 86.96, currency: 'SAR' },
            vat: {
              vatAmount: { amount: 13.04, currency: 'SAR' },
              vatPercentage: 15
            },
            total: { amount: 100, currency: 'SAR' }
          },
          applyReservedTime: false,
          isCancelled: false,
          violationType: { englishName: 'Incorrect Parking Violation', arabicName: 'Incorrect Parking Violation' }
        }
      ],
      reservation: null,
      customerName: 'John Doe',
      customerPhoneNumber: '+966500000000',
      operationalFees: [],
      status: 'Active',
      allowedToPay: true,
      isSettled: false
    };
  }
}
